import axios from "axios";
import { produce } from "immer";

import SearchActionTypes from "./action-types";
import { SearchAction } from "./actions";
import { ISearchParams } from "./api";
import { ISearchHeaders, parseSearchHeaders } from "./utils";
import { checksum } from "src/utils/checksum";
import { SearchResponse, AiSearchSummary } from "./types";

export interface ISearchState {
    currentResults?: Spintr.ISearchResult[];
    currentAiResults?: Spintr.ISearchResult[];
    currentAiSummary?: AiSearchSummary;
    currentHeaders?: ISearchHeaders;
    errorMessageTag?: string;
    isLoadingResults: boolean;
    lastSuccessfulParams?: ISearchParams;
    onedriveResults?: any[];
    sharepointResults?: any[];
    isActive?: boolean;

    history: {
        [checksum: number]: {
            params: ISearchParams;
            results: Spintr.ISearchResult[];
            aiResults?: Spintr.ISearchResult[];
            aiSummary?: AiSearchSummary;
            headers?: ISearchHeaders;
            lastAccessed: Date;
        };
    };
}

const initialState: ISearchState = {
    isLoadingResults: false,
    history: {},
};

export const searchReducer =
    (state = initialState, action: SearchAction): ISearchState => produce(
        state, 
        (draft) => {
            switch (action.type) {
                case SearchActionTypes.LoadCachedResult:
                    const cachedResult = state.history[action.checksum];
                    if (!cachedResult) {
                        draft.currentResults = undefined;
                        draft.currentAiResults = undefined;
                        draft.currentHeaders = undefined;
                    } else {
                        draft.currentResults = cachedResult.results;
                        draft.currentAiResults = cachedResult.aiResults;
                        draft.currentHeaders = cachedResult.headers;
                        draft.lastSuccessfulParams = cachedResult.params;
                        draft.isLoadingResults = false;
                        draft.isActive = true;

                        draft.history[action.checksum] = {
                            ...cachedResult,
                            // Update last accessed time
                            lastAccessed: new Date(),
                        };
                    }
                    break;

                case SearchActionTypes.ClearSearchCache:
                    draft.history = {};
                    draft.currentResults = undefined;
                    draft.currentAiResults = undefined;
                    draft.currentHeaders = undefined;
                    draft.lastSuccessfulParams = undefined;
                    draft.errorMessageTag = undefined;
                    draft.isLoadingResults = false;
                    draft.isActive = false;
                    break;

                case SearchActionTypes.SetAiSummary:
                    draft.currentAiSummary = action.summary;
                    if (action.checksum && draft.history[action.checksum]) {
                        draft.history[action.checksum].aiSummary = action.summary;
                    }
                    break;

                case SearchActionTypes.ClearCurrentResults:
                    draft.currentResults = undefined;
                    draft.currentAiResults = undefined;
                    draft.currentHeaders = undefined;
                    draft.lastSuccessfulParams = undefined;
                    draft.errorMessageTag = undefined;
                    draft.isLoadingResults = false;
                    draft.isActive = false;
                    break;

                case SearchActionTypes.ExecuteSearchFulfilled:
                    draft.currentHeaders = parseSearchHeaders(
                        action.payload.headers as { [name: string]: string },
                    );

                    const appendResults = action.meta.skip > 0;

                    let spintrResults: Spintr.ISearchResult[] = [];
                    let aiResults: Spintr.ISearchResult[] | undefined;
                    const response: SearchResponse = action.payload.data;

                    if (typeof response === "object" && "spintrResults" in response) {
                        spintrResults = response.spintrResults;
                        aiResults = response.aiResults;
                    } else {
                        spintrResults = response;
                    }

                    draft.currentAiResults = aiResults;

                    if (appendResults) {
                        draft.currentResults = state.currentResults
                            .concat(spintrResults);
                    } else {
                        draft.currentResults = spintrResults;
                    }

                    draft.errorMessageTag = undefined;
                    draft.isLoadingResults = false;
                    draft.lastSuccessfulParams = action.meta;

                    const searchParams = action.meta;
                    const code = checksum(searchParams);

                    draft.history[code] = {
                        params: searchParams,
                        results: spintrResults,
                        aiResults: aiResults,
                        headers: draft.currentHeaders,
                        lastAccessed: new Date(),
                    };
                    break;

                case SearchActionTypes.ExecuteSearchPending:
                    draft.errorMessageTag = undefined;
                    draft.isLoadingResults = true;
                    draft.isActive = true;
                    break;

                case SearchActionTypes.ExecuteSearchRejected:
                    // TODO: switch over action.payload for exact reason
                    console.log(action.payload);
                    if (axios.isCancel(action.payload)) {
                        // don't show error message if the request was cancelled
                        // due to a new search request before the previous request finished
                        break;
                    }

                    draft.currentResults = undefined;
                    draft.currentAiResults = undefined;
                    draft.currentHeaders = undefined;
                    draft.errorMessageTag = "TeknisktFel";
                    draft.isLoadingResults = false;
                    break;
                
                case SearchActionTypes.SetOnedriveResults:
                    draft.onedriveResults = action.results;
                    break;
                case SearchActionTypes.SetSharepointResults:
                    draft.sharepointResults = action.results;
                    break;
            }
        },
    );
